import React from 'react';
import useTranslation from 'next-translate/useTranslation';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import { WPImage } from '@/data/wp/operations/queries/fragments/image';
import { AngleImage } from '@/types/common';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { getIconComponentByName } from '@/utils/icons-map';
import styled from '@mui/system/styled';
import Image from 'next/image';
import useMediaQuery from '@mui/material/useMediaQuery';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import theme from '@/mui/theme';
import ProductDetailModal from '@/components/fasce/ProductDetailModal';
import Button from '@mui/material/Button';

interface ProductDetailTableProps {
  singleTable: {
    tabTitle: string;
    tableInfo?: {
      th: string[];
      tr: {
        immagine?: WPImage;
        nome?: string;
        descrizione?: string;
        sku?: string;
        merchUrl?: string;
        openModalLabel?: string;
        storeId: string;
        langId: string;
        breadcrumbs: any;
        attachments: any;
        parentProductName: string;
        fullImage: string;
        thumbnail: string;
        angleImages?: AngleImage[];
        appUso: string;
        cablaggio?: string;
        siglaCablaggio?: string,
        kg?: string;
        correnteNominaleDriver?: string;
        gradiAsimmetria?: string,
        lumen?: string;
        aperturaFascio?: string;
        wtot?: string;
        potenza?: string,
        colore?: string;
        lunghezza?: string;
        larghezza?: string;
        altezza?: string;
        diametro?: string;
        paloAltezzaFuoriTerra?: string;
        paloAltezzaInterrata?: string;
        paloLunghezzaTotale?: string;
        garanzia?: string;
        download?: string;
      }[];
      activeColumns : {
        cablaggio: boolean,
        siglaCablaggio: boolean,
        colore: boolean,
        garanzia: boolean,
        correnteNominaleDriver: boolean,
        led: boolean,
        aperturaFascio: boolean,
        gradiAsimmetria: boolean,
        peso: boolean,
        wTot: boolean,
        potenza: boolean,
        lunghezza: boolean,
        larghezza: boolean,
        altezza: boolean,
        diametro: boolean,
        paloAltezzaFuoriTerra: boolean,
        paloAltezzaInterrata: boolean,
        paloLunghezzaTotale: boolean,
        download: boolean
      };
    };
    dimensioniImg?: WPImage;
    dimensioniTable?: {
      name?: string;
      value?: string;
    }[];
  }[];
};

interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-detail-tab-${index}`}
      aria-labelledby={`product-detail-tabpanel-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 5 }}>
          <Box>{children}</Box>
        </Box>
      )}
    </div>
  );
};

const StyledTabTitle = styled(Tabs)({
  'button': {
    color: '#182641',
    fontSize: '24px',
    fontWeight: 'bold',
    letterSpacing: '-0.1px',
    lineHeight: '30px',
    '&.MuiTab-root.Mui-selected': {
      color: '#182641',
    }
  },
  '.MuiTabs-indicator': {
    backgroundColor: '#1288CF',
    bottom: '2px',
    height: '4px',
  },
  '.MuiTabs-flexContainer': {
    borderBottom: '1px solid #d4d4d4',
    marginBottom: '5px',
  }
});

const StyledTableHead = styled(TableHead)({
  [theme.breakpoints.down('sm')]: {
    '.tab-0 & th': {
      '&:not(:first-of-type):not(:last-of-type):not(:nth-last-of-type(2))': {
        display: 'none',
      }
    },
    '.tab-1 & th': {
      '&:not(:first-of-type):not(:nth-of-type(2))': {
        display: 'none',
      }
    },
  },
  backgroundColor: '#343d4e',
  height: '63px',
  'th': {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    letterSpacing: '0',
    lineHeight: '17px',
    backgroundColor: '#343d4e',
    border: '0',
  },
});

const StyledTableRow = styled(TableRow)({
  [theme.breakpoints.down('sm')]: {
    '.tab-0 & td': {
      '&:not(:last-of-type):not(:nth-last-of-type(2))': {
        display: 'none',
      }
    },
    '.tab-1 & td': {
      display: 'none',
    },
  },
  backgroundColor: '#f1f1f1',
  '&:nth-of-type(odd)': {
    backgroundColor: '#ffffff0d',
  },
  '.MuiTableCell-root': {
    border: '0',
  },
  '.MuiLink-root': {
    fontSize: '16px',
    letterSpacing: '0.1px',
    lineHeight: '24px',
    textDecoration: 'unset',
  },
  '.MuiTableCell-body': {
    color: '#000000',
    fontSize: '16px',
    letterSpacing: '0.1px',
    lineHeight: '24px',
    '.MuiButton-root': {
      padding: '0',
      justifyContent: 'flex-start',
      transition: 'unset',
      backgroundColor: 'transparent',
      'span': {
        display: 'none'
      }
    }
  }
});

const StyledTabPanel = styled(TabPanel)({
  '&.tab-0': {
    '.MuiTableHead-root': {
      'th': {
        '&:last-of-type, &:nth-last-of-type(1)': {
          textAlign: 'center',
        },
      }
    }
  },
  '&.tab-1': {
    [theme.breakpoints.up('sm')]: {
      '.MuiTableHead-root': {
        'th': {
          minWidth: '200px'
        }
      },
    },
    '.MuiTableBody-root': {
      'th:first-of-type': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }
    }
  }
});

const StyledTableContainer = styled(TableContainer)({
  '.MuiTableRow-root': {
    borderBottom: '1px solid #f5f5f5',
    'td': {
      padding: '12px 0',
      border: '0',
      fontSize: '16px',
      letterSpacing: '0.1px',
      color: '#000000',
      '&:first-of-type': {
        fontWeight: '600',
      }
    }
  }
});

const StyledTitle = styled(Typography)({
  [theme.breakpoints.up('sm')]: {
    fontSize: '24px',
    letterSpacing: '-0.1px',
    lineHeight: '30px',
  },
  color: '#182641',
  fontSize: '16px',
  fontWeight: '800',
  letterSpacing: '0.1px',
  lineHeight: '24px',
});

const StyledAccordion = styled(Accordion)({
  backgroundColor: '#f1f1f1',
  border: '0',
  boxShadow: 'unset',
  marginBottom: '3px',
  '.MuiAccordionDetails-root': {
    padding: '0',
    backgroundColor: '#ffffff',
    '.MuiBox-root': {
      paddingTop: '0',
    }
  },
  '&::before': {
    display: 'none',
  }
});

const StyledAccordionSummary = styled(AccordionSummary)({
  "&.Mui-expanded .Mui-expanded": {
    transform: "rotate(0deg)",
  },
});

const StyledTableCell = styled(TableCell)({
  'a': {
    color: '#000000'
  }
});

const ProductDetailTable: React.FC<ProductDetailTableProps> = ({ singleTable }) => {
  const { t } = useTranslation('common');
  const matches = useMediaQuery('(min-width:600px)');
  const [value, setValue] = React.useState(0);
  // @ts-ignore
  const handleChange = (event: any, newValue: React.SetStateAction<number>) => {
    setValue(newValue);
  };

  const subtitle = t(`flux-table-subtitle`, {}, { default: 'Il flusso luminoso riportato indica il flusso uscente dall’apparecchio con una tolleranza di ± 10% rispetto al valore indicato. I W tot sono la potenza totale assorbita dal sistema e non supera il 10% del valore indicato.' });

  return (
    <>
      {singleTable && singleTable.length > 1 &&
        <Grid item xs={12}>
          {matches && <>
            <Box>
              <StyledTabTitle value={value} onChange={handleChange} scrollButtons={false} aria-label={t('product-detail-tabs', {}, { default: 'product detail tabs' })}>
                {singleTable && singleTable.map((tableTab, index) => <Tab label={tableTab.tabTitle} id={`product-detail-tab-${index}`} aria-controls={`product-detail-tabpanel-${index}`} key={index} />)}
              </StyledTabTitle>
            </Box>
            {singleTable && singleTable.map((activeTable, index) => {
              if (activeTable.tableInfo !== undefined) {
                return <StyledTabPanel className={`tab-${index}`} value={value} index={index} key={index}>
                  <TableContainer>
                    <Table aria-label={t('product-detail-table', {}, { default: 'product detail table' })}>
                      <StyledTableHead>
                        <TableRow>
                          {activeTable.tableInfo && activeTable.tableInfo.th &&
                            activeTable.tableInfo.th.map((th, index) => {
                                if (index === 0 ){
                                  return <TableCell key={index}>{th}</TableCell>
                                } else {
                                  return <TableCell align="center" key={index}>{th}</TableCell>
                                }
                              }
                            )
                          }
                        </TableRow>
                      </StyledTableHead>
                      <TableBody>
                        {activeTable.tableInfo && activeTable.tableInfo.tr && activeTable.tableInfo.tr.map((singleRow) => {
                          return <StyledTableRow key={singleRow.sku}>
                            {singleRow.immagine && singleRow.immagine.mediaItemUrl &&
                              <TableCell component="th" scope="row">
                                <Image
                                  src={singleRow.immagine.mediaItemUrl}
                                  width={singleRow.immagine.mediaDetails.width}
                                  height={singleRow.immagine.mediaDetails.height}
                                  alt={singleRow.immagine.altText}
                                />
                              </TableCell>
                            }
                            {singleRow.sku && !singleRow.merchUrl &&
                              <TableCell component="th" scope="row">
                                <ProductDetailModal sku={singleRow.sku} openModalLabel={singleRow.openModalLabel || singleRow.sku} storeId={singleRow.storeId} langId={singleRow.langId} breadcrumbs={singleRow.breadcrumbs} attachments={singleRow.attachments} garanzia={singleRow.garanzia} defaultImage={singleRow.fullImage} appUso={singleRow.appUso} defaultThumb={singleRow.thumbnail} angleImages={singleRow.angleImages} parentProductName={singleRow.parentProductName} />
                              </TableCell>
                            }
                            {singleRow.merchUrl &&
                              <TableCell component="th" scope="row">
                                <Button href={singleRow.merchUrl}>{singleRow.nome}</Button>
                              </TableCell>
                            }
                            {singleRow.nome && !singleRow.merchUrl &&
                              <TableCell align="center">{singleRow.nome}</TableCell>
                            }
                            {singleRow.descrizione &&
                              <TableCell align="center">{singleRow.descrizione}</TableCell>
                            }
                            {singleRow.cablaggio && activeTable.tableInfo?.activeColumns.cablaggio &&
                              <TableCell align="center">{singleRow.cablaggio}</TableCell>
                            }
                            {singleRow.siglaCablaggio && activeTable.tableInfo?.activeColumns.siglaCablaggio &&
                              <TableCell align="center">{singleRow.siglaCablaggio}</TableCell>
                            }
                            {singleRow.kg && activeTable.tableInfo?.activeColumns.peso &&
                              <TableCell align="center">{singleRow.kg}</TableCell>
                            }
                            {singleRow.correnteNominaleDriver && activeTable.tableInfo?.activeColumns.correnteNominaleDriver &&
                               <TableCell align="center">{singleRow.correnteNominaleDriver}</TableCell>
                            }
                            {singleRow.lumen && activeTable.tableInfo?.activeColumns.led &&
                              <TableCell align="center">{singleRow.lumen}</TableCell>
                            }
                            {singleRow.aperturaFascio && activeTable.tableInfo?.activeColumns.aperturaFascio &&
                               <TableCell align="center">{singleRow.aperturaFascio}</TableCell>
                            }
                            {singleRow.gradiAsimmetria && activeTable.tableInfo?.activeColumns.gradiAsimmetria &&
                               <TableCell align="center">{singleRow.gradiAsimmetria}</TableCell>
                            }
                            {singleRow.wtot && activeTable.tableInfo?.activeColumns.wTot &&
                              <TableCell align="center">{singleRow.wtot}</TableCell>
                            }
                            {singleRow.potenza && activeTable.tableInfo?.activeColumns.potenza &&
                               <TableCell align="center">{singleRow.potenza}</TableCell>
                            }
                            {singleRow.colore && activeTable.tableInfo?.activeColumns.colore &&
                              <TableCell align="center">{singleRow.colore}</TableCell>
                            }
                            {singleRow.lunghezza && activeTable.tableInfo?.activeColumns.lunghezza &&
                              <TableCell align="center">{singleRow.lunghezza}</TableCell>
                            }
                            {singleRow.larghezza && activeTable.tableInfo?.activeColumns.larghezza &&
                              <TableCell align="center">{singleRow.larghezza}</TableCell>
                            }
                            {singleRow.altezza && activeTable.tableInfo?.activeColumns.altezza &&
                              <TableCell align="center">{singleRow.altezza}</TableCell>
                            }
                            {singleRow.diametro && activeTable.tableInfo?.activeColumns.diametro &&
                              <TableCell align="center">{singleRow.diametro}</TableCell>
                            }
                            {singleRow.paloAltezzaFuoriTerra && activeTable.tableInfo?.activeColumns.paloAltezzaFuoriTerra &&
                              <TableCell align="center">{singleRow.paloAltezzaFuoriTerra}</TableCell>
                            }
                            {singleRow.paloAltezzaInterrata && activeTable.tableInfo?.activeColumns.paloAltezzaInterrata &&
                              <TableCell align="center">{singleRow.paloAltezzaInterrata}</TableCell>
                            }
                            {singleRow.paloLunghezzaTotale && activeTable.tableInfo?.activeColumns.paloLunghezzaTotale &&
                              <TableCell align="center">{singleRow.paloLunghezzaTotale}</TableCell>
                            }
                            {singleRow.garanzia && false &&
                              <TableCell align="center">2<a href={singleRow.garanzia}>{getIconComponentByName('document')}</a></TableCell>
                            }
                            {singleRow.download &&
                              <StyledTableCell align="center"><a href="" download={singleRow.download}>{getIconComponentByName('download')}</a></StyledTableCell>
                            }
                          </StyledTableRow>
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </StyledTabPanel>
              } else {
                return <StyledTabPanel className={`tab-${index}`} value={value} index={index} key={index}>
                  <Grid container rowSpacing={0} sx={{ position: 'relative' }}>
                    <Grid item xs={12} sm={6} sx={{ position: 'relative' }}>
                      {activeTable?.dimensioniImg && activeTable?.dimensioniImg.mediaItemUrl &&
                        <Image
                          layout="responsive"
                          src={activeTable?.dimensioniImg.mediaItemUrl}
                          width={activeTable?.dimensioniImg.mediaDetails.width}
                          height={activeTable?.dimensioniImg.mediaDetails.height}
                          alt={activeTable.dimensioniImg.altText}
                        />
                      }
                    </Grid>
                    <Grid item xs={0} sm={1}></Grid>
                    <Grid item xs={12} sm={5} display="flex" justifyContent="center" alignItems="center">
                      <StyledTableContainer>
                        <Table aria-label={t('product-detail-dimension-table', {}, { default: 'product detail dimension table' })}>
                          <TableBody>
                            {activeTable.dimensioniTable && activeTable.dimensioniTable.map((row, index) => {
                              return <TableRow key={index} sx={{ display: "flex" }}>
                                <TableCell sx={{ minWidth: '200px' }}>
                                  {row.name}
                                </TableCell>
                                <TableCell>
                                  {row.value}
                                </TableCell>
                              </TableRow>
                            })}
                          </TableBody>
                        </Table>
                      </StyledTableContainer>
                    </Grid>
                  </Grid>
                </StyledTabPanel>
              }
            })}
          </>}
          {!matches && singleTable.map((tableTab, index) => {
            return <StyledAccordion
              square={true}
              disableGutters={true}
              key={index}
            >
              <StyledAccordionSummary
                expandIcon={getIconComponentByName('plus')}
              >
                <StyledTitle>{tableTab.tabTitle}</StyledTitle>
              </StyledAccordionSummary>
              <AccordionDetails>
                {index != 2 &&
                  <StyledTabPanel className={`tab-${index}`} value={index} index={index}>
                    <TableContainer>
                      <Table aria-label={t('product-detail-table', {}, { default: 'product detail table' })}>
                        <StyledTableHead>
                          <TableRow>
                            {tableTab.tableInfo && tableTab.tableInfo.th &&
                              tableTab.tableInfo.th.map((th, index) => <TableCell key={index}>{th}</TableCell>)
                            }
                          </TableRow>
                        </StyledTableHead>
                        <TableBody>
                          {tableTab.tableInfo && tableTab.tableInfo.tr && tableTab.tableInfo.tr.map((singleRow, index) => {
                            return <StyledTableRow key={index}>
                              {singleRow.immagine && singleRow.immagine.mediaItemUrl &&
                                <TableCell component="th" scope="row">
                                  <Image
                                    src={singleRow.immagine.mediaItemUrl}
                                    width={singleRow.immagine.mediaDetails.width}
                                    height={singleRow.immagine.mediaDetails.height}
                                    alt={singleRow.immagine.altText}
                                  />
                                </TableCell>
                              }
                              {singleRow.sku && !singleRow.merchUrl &&
                                <TableCell component="th" scope="row">
                                  <ProductDetailModal sku={singleRow.sku} openModalLabel={singleRow.openModalLabel || singleRow.sku} storeId={singleRow.storeId} langId={singleRow.langId} breadcrumbs={singleRow.breadcrumbs} attachments={singleRow.attachments} garanzia={singleRow.garanzia} appUso={singleRow.appUso} defaultImage={singleRow.fullImage} defaultThumb={singleRow.thumbnail} angleImages={singleRow.angleImages} parentProductName={singleRow.parentProductName} />
                                </TableCell>
                              }
                              {singleRow.merchUrl &&
                                <TableCell component="th" scope="row">
                                  <Button href={singleRow.merchUrl}>{singleRow.nome}</Button>
                                </TableCell>
                              }
                              {singleRow.nome && !singleRow.merchUrl &&
                                <TableCell align="center">{singleRow.nome}</TableCell>
                              }
                              {singleRow.cablaggio && tableTab.tableInfo?.activeColumns.cablaggio &&
                                <TableCell align="center">{singleRow.cablaggio}</TableCell>
                              }
                              {singleRow.siglaCablaggio && tableTab.tableInfo?.activeColumns.siglaCablaggio &&
                                <TableCell align="center">{singleRow.siglaCablaggio}</TableCell>
                              }
                              {singleRow.kg && tableTab.tableInfo?.activeColumns.peso &&
                                <TableCell align="center">{singleRow.kg}</TableCell>
                              }
                              {singleRow.correnteNominaleDriver && tableTab.tableInfo?.activeColumns.correnteNominaleDriver &&
                                <TableCell align="center">{singleRow.correnteNominaleDriver}</TableCell>
                              }
                              {singleRow.lumen && tableTab.tableInfo?.activeColumns.led &&
                                <TableCell align="center">{singleRow.lumen}</TableCell>
                              }
                              {singleRow.aperturaFascio && tableTab.tableInfo?.activeColumns.aperturaFascio &&
                                <TableCell align="center">{singleRow.aperturaFascio}</TableCell>
                              }
                              {singleRow.wtot && tableTab.tableInfo?.activeColumns.wTot &&
                                <TableCell align="center">{singleRow.wtot}</TableCell>
                              }
                              {singleRow.lunghezza && tableTab.tableInfo?.activeColumns.lunghezza &&
                                <TableCell align="center">{singleRow.lunghezza}</TableCell>
                              }
                              {singleRow.larghezza && tableTab.tableInfo?.activeColumns.larghezza &&
                                <TableCell align="center">{singleRow.larghezza}</TableCell>
                              }
                              {singleRow.altezza && tableTab.tableInfo?.activeColumns.altezza &&
                                <TableCell align="center">{singleRow.altezza}</TableCell>
                              }
                              {singleRow.diametro && tableTab.tableInfo?.activeColumns.diametro &&
                                <TableCell align="center">{singleRow.diametro}</TableCell>
                              }
                              {singleRow.paloAltezzaFuoriTerra && tableTab.tableInfo?.activeColumns.paloAltezzaFuoriTerra &&
                                <TableCell align="center">{singleRow.paloAltezzaFuoriTerra}</TableCell>
                              }
                              {singleRow.paloAltezzaInterrata && tableTab.tableInfo?.activeColumns.paloAltezzaInterrata &&
                                <TableCell align="center">{singleRow.paloAltezzaInterrata}</TableCell>
                              }
                              {singleRow.paloLunghezzaTotale && tableTab.tableInfo?.activeColumns.paloLunghezzaTotale &&
                                <TableCell align="center">{singleRow.paloLunghezzaTotale}</TableCell>
                              }
                              {singleRow.colore && tableTab.tableInfo?.activeColumns.colore &&
                                <TableCell align="center">{singleRow.colore}</TableCell>
                              }
                              {singleRow.garanzia && false &&
                                <TableCell align="center">1<a href={singleRow.garanzia}>{getIconComponentByName('document')}</a></TableCell>
                              }
                              {singleRow.download &&
                                <StyledTableCell align="center"><a href={"/download/" + singleRow.download + "/download"} download>{getIconComponentByName('download')}</a></StyledTableCell>
                              }
                            </StyledTableRow>
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </StyledTabPanel>
                }
                {index == 2 &&
                  <StyledTabPanel className={`tab-${index}`} value={index} index={index}>
                    <Grid container rowSpacing={0} sx={{ position: 'relative' }}>
                      <Grid item xs={12} sm={6} sx={{ position: 'relative' }}>
                        {tableTab?.dimensioniImg && tableTab.dimensioniImg.mediaItemUrl &&
                          <Image
                            layout="responsive"
                            src={tableTab.dimensioniImg.mediaItemUrl}
                            width={tableTab.dimensioniImg.mediaDetails.width}
                            height={tableTab.dimensioniImg.mediaDetails.height}
                            alt={tableTab.dimensioniImg.altText}
                          />
                        }
                      </Grid>
                      <Grid item xs={0} sm={1}></Grid>
                      <Grid item xs={12} sm={5} display="flex" justifyContent="center" alignItems="center">
                        <StyledTableContainer>
                          <Table aria-label={t('product-detail-dimension-table', {}, { default: 'product detail dimension table' })}>
                            <TableBody>
                              {tableTab.dimensioniTable && tableTab.dimensioniTable.map((row, index) => {
                                return <TableRow key={index} sx={{ display: "flex" }}>
                                  <TableCell sx={{ minWidth: '200px' }}>
                                    {row.name}
                                  </TableCell>
                                  <TableCell>
                                    {row.value}
                                  </TableCell>
                                </TableRow>
                              })}
                            </TableBody>
                          </Table>
                        </StyledTableContainer>
                      </Grid>
                    </Grid>
                  </StyledTabPanel>
                }
              </AccordionDetails>
            </StyledAccordion>
          })}
          <Typography variant="body1" mt={5}>{subtitle}</Typography>
        </Grid>
      }
    </>
  )
}

export default ProductDetailTable;
